// Pricing — Public Access Lockdown (Waitlist-Only Mode)
//
// Public, informational pricing page — accessible without signing in.
// Four-column layout: Free | Investor | Professional | Enterprise.
//
// The platform is pre-launch / audience-acquisition only, so this page no
// longer converts visitors into accounts: it does NOT route to /login, drive
// Stripe checkout, or use any trial / Start Free / Get Started / Upgrade
// language. Every tier card surfaces a single "Join Waitlist" CTA that sends
// the visitor to the Landing waitlist (the sole approved conversion path).
//
// The subscription / checkout architecture is untouched and still lives in
// SubscriptionContext + the Account billing surface, so controlled onboarding
// (private beta / invite-only) can re-introduce upgrade CTAs here later.
//
// This route is rendered OUTSIDE the Layout shell (public route in App.js).

import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { track } from '../lib/analytics';
import {
  captureAttribution,
  setPlanIntent,
  trackFunnel,
  FUNNEL_EVENTS,
} from '../lib/attribution';
import { buildComparisonRows, PLAN_CATALOG } from '../lib/planCatalog';

// Where the waitlist lives — the Landing page waitlist section (#waitlist).
const WAITLIST_TARGET = '/#waitlist';

const FEATURES = {
  free: [
    'Full-access property evaluation',
    'Run property analyses',
    'View scenario comparisons',
    'Basic deal scoring',
  ],
  investor: [
    'Unlimited analyses',
    'Save & revisit analyses',
    'Full forecasting & DSCR engine',
    'Deal scoring (A–F grades)',
    'Scenario comparison (Base / Optimized / Aggressive)',
    'Address autocomplete & geo search',
    'PDF investor reports (coming soon)',
  ],
  professional: [
    'Everything in Investor',
    'Priority support',
    'API access',
    'Bulk analysis runs',
    'Portfolio analytics',
  ],
  enterprise: [
    'Everything in Professional',
    'Custom integrations',
    'Dedicated account manager',
    'Custom contracts & SLAs',
    'White-label options',
  ],
};

export default function Pricing() {
  const navigate = useNavigate();

  // BL-038 Story 38.1 — capture first-touch source attribution and record the
  // pricing-page view as the funnel's top stage (advisory; no PostHog key → no-op).
  useEffect(() => {
    captureAttribution();
    trackFunnel(FUNNEL_EVENTS.PRICING_VIEW, {});
  }, []);

  // Sole conversion action on this page: send the visitor to the Landing
  // waitlist. No account is created and no /login route is involved. BL-038
  // Story 38.2/38.3 — additionally record WHICH plan the visitor chose (a
  // non-billing demand signal) so it carries through to the waitlist submit.
  function handleJoinWaitlist(tier) {
    if (tier) {
      setPlanIntent(tier);
      trackFunnel(FUNNEL_EVENTS.PLAN_SELECTED, { tier });
    }
    track('pricing_waitlist_click');
    navigate(WAITLIST_TARGET);
  }

  // BL-038 Story 38.3 — invite-aware affordance. Invited cohort members can
  // jump to the controlled-onboarding entry (/onboarding) instead of the
  // waitlist. This does NOT weaken Public Access Lockdown: /onboarding is the
  // existing PUBLIC explainer that only offers the cohort-gated OAuth flow —
  // there is no public account creation here and no route to /login.
  function handleInvitePath() {
    trackFunnel(FUNNEL_EVENTS.INVITE_PATH, { from: 'pricing' });
  }

  return (
    <div
      style={{
        background: 'var(--color-navy)',
        minHeight: '100vh',
        padding: 'var(--space-8) var(--space-6)',
        color: 'var(--color-white)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* ── Header ────────────────────────────────────────── */}
        <header
          style={{
            textAlign: 'center',
            marginBottom: 'var(--space-10)',
          }}
        >
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-5xl)',
              fontWeight: 'var(--weight-black)',
              letterSpacing: 'var(--tracking-tight)',
              textTransform: 'uppercase',
              color: 'var(--color-white)',
              margin: 0,
              marginBottom: 'var(--space-3)',
            }}
          >
            Simple, transparent pricing
          </h1>
          <p
            style={{
              fontSize: 'var(--text-lg)',
              color: 'var(--color-steel-lt)',
              margin: 0,
              lineHeight: 'var(--leading-normal)',
            }}
          >
            Plans shown for reference while we open access in stages. Join the
            waitlist to be first in line.
          </p>
        </header>

        {/* ── Cards ─────────────────────────────────────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 'var(--space-5)',
            alignItems: 'stretch',
          }}
        >
          <FreeTierCard onJoinWaitlist={handleJoinWaitlist} />
          <InvestorTierCard onJoinWaitlist={handleJoinWaitlist} />
          <ProfessionalTierCard onJoinWaitlist={handleJoinWaitlist} />
          <EnterpriseTierCard onJoinWaitlist={handleJoinWaitlist} />
        </div>

        {/* ── Plan comparison (entitlement communication) ───── */}
        <PlanComparison />

        {/* ── Invite-aware affordance (Story 38.3) ──────────── */}
        <div
          style={{
            textAlign: 'center',
            marginTop: 'var(--space-8)',
            color: 'var(--color-steel-lt)',
            fontSize: 'var(--text-base)',
          }}
        >
          Already invited to the controlled onboarding?{' '}
          <Link
            to="/onboarding"
            onClick={handleInvitePath}
            style={{ color: 'var(--color-teal)', fontWeight: 'var(--weight-semibold)' }}
          >
            Activate your invitation
          </Link>
          .
        </div>

        {/* ── Fine print ────────────────────────────────────── */}
        <p
          style={{
            textAlign: 'center',
            color: 'var(--color-muted)',
            fontSize: 'var(--text-sm)',
            marginTop: 'var(--space-8)',
          }}
        >
          Pricing and availability are finalized at launch. Prices in USD.
        </p>
      </div>
    </div>
  );
}

// Shared waitlist CTA used by every tier card — the only call to action on
// the public pricing surface.
function WaitlistCta({ onJoinWaitlist }) {
  return (
    <button
      type="button"
      className="btn btn-primary"
      style={btnFull}
      onClick={onJoinWaitlist}
    >
      Join Waitlist
    </button>
  );
}

// =========================
// FREE TIER CARD — plain card, visually de-emphasized
// =========================
export function FreeTierCard({ onJoinWaitlist }) {
  return (
    <TierCardShell variant="plain">
      <TierHeader label="Free" price="$0" period="entry tier" />
      <TierDescription>
        A free entry point into the investor workspace to evaluate properties
        and see exactly what the paid tiers unlock.
      </TierDescription>
      <FeatureList items={FEATURES.free} />
      <WaitlistCta onJoinWaitlist={() => onJoinWaitlist('free')} />
    </TierCardShell>
  );
}

// =========================
// INVESTOR TIER CARD — $49/mo, highlighted with .card-accent + "Most Popular"
// =========================
export function InvestorTierCard({ onJoinWaitlist }) {
  return (
    <TierCardShell variant="accent">
      {/* Most Popular badge — pinned at the top center */}
      <span
        className="badge"
        style={{
          position: 'absolute',
          top: 'calc(-1 * var(--space-3))',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--color-amber)',
          color: 'var(--color-navy)',
          border: '1px solid var(--color-amber)',
        }}
      >
        Most Popular
      </span>

      <TierHeader label="Investor" price="$49" period="per month" priceColor="var(--color-amber)" />
      <TierDescription>
        Everything you need to underwrite RV and outdoor hospitality deals, save your analyses,
        and move from research to decision.
      </TierDescription>
      <FeatureList items={FEATURES.investor} />
      <WaitlistCta onJoinWaitlist={() => onJoinWaitlist('investor')} />
    </TierCardShell>
  );
}

// =========================
// PROFESSIONAL TIER CARD — $99/mo (informational; waitlist CTA only)
// =========================
export function ProfessionalTierCard({ onJoinWaitlist }) {
  return (
    <TierCardShell variant="plain">
      <TierHeader label="Professional" price="$99" period="per month" />
      <TierDescription>
        For teams and active investors who need priority support, API access,
        and bulk analysis on top of everything in Investor.
      </TierDescription>
      <FeatureList items={FEATURES.professional} />
      <WaitlistCta onJoinWaitlist={() => onJoinWaitlist('professional')} />
    </TierCardShell>
  );
}

// =========================
// ENTERPRISE TIER CARD — custom pricing (informational; waitlist CTA only)
// =========================
export function EnterpriseTierCard({ onJoinWaitlist }) {
  return (
    <TierCardShell variant="plain">
      <TierHeader label="Enterprise" price="Custom" period=" " />
      <TierDescription>
        Custom integrations, dedicated account management, and tailored
        contracts for high-volume pipelines and multi-seat teams.
      </TierDescription>
      <FeatureList items={FEATURES.enterprise} />
      <WaitlistCta onJoinWaitlist={() => onJoinWaitlist('enterprise')} />
    </TierCardShell>
  );
}

// =========================
// SHARED SUBCOMPONENTS
// =========================

// TierCardShell — wraps each tier card with .card (plain) or .card-accent (highlighted)
function TierCardShell({ variant, current, children }) {
  return (
    <div
      className={variant === 'accent' ? 'card-accent' : 'card'}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        // Highlighted card sits 1px higher visually via the accent border;
        // explicit min-height keeps the 3 cards aligned at their tops.
        ...(current && variant !== 'accent' ? { borderColor: 'var(--color-amber)' } : {}),
      }}
    >
      {children}
    </div>
  );
}

function TierHeader({ label, price, period, priceColor }) {
  return (
    <>
      <div
        style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--weight-bold)',
          color: 'var(--color-steel)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-label)',
          marginBottom: 'var(--space-2)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-5xl)',
          fontWeight: 'var(--weight-black)',
          color: priceColor || 'var(--color-white)',
          lineHeight: 'var(--leading-none)',
        }}
      >
        {price}
      </div>
      <div
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--color-muted)',
          marginTop: 'var(--space-1)',
          marginBottom: 'var(--space-4)',
        }}
      >
        {period}
      </div>
    </>
  );
}

function TierDescription({ children }) {
  return (
    <p
      style={{
        fontSize: 'var(--text-base)',
        color: 'var(--color-steel-lt)',
        lineHeight: 'var(--leading-normal)',
        margin: 0,
        marginBottom: 'var(--space-5)',
      }}
    >
      {children}
    </p>
  );
}

function FeatureList({ items }) {
  return (
    <ul
      style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        marginBottom: 'var(--space-5)',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      {items.map((text) => (
        <li
          key={text}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-2)',
            fontSize: 'var(--text-base)',
            color: 'var(--color-white-dim)',
            lineHeight: 'var(--leading-normal)',
          }}
        >
          <CheckIcon />
          <span>{text}</span>
        </li>
      ))}
    </ul>
  );
}

// Consistent checkmark icon — same color across all tiers per spec.
function CheckIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      width="16"
      height="16"
      aria-hidden="true"
      style={{ flexShrink: 0, marginTop: '2px' }}
    >
      <path
        d="M4 10.5l3.5 3.5L16 5"
        fill="none"
        stroke="var(--color-success-lt)"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// =========================
// PLAN COMPARISON — capability matrix (BL-038 Story 38.2)
// Entitlement communication sourced from the single catalog (lib/planCatalog →
// lib/entitlements). Display only; it never decides access. No trial / upgrade /
// checkout language — the page stays under Public Access Lockdown.
// =========================
function PlanComparison() {
  const rows = buildComparisonRows();
  const cellBase = {
    padding: 'var(--space-2) var(--space-3)',
    textAlign: 'center',
    fontSize: 'var(--text-sm)',
    borderTop: '1px solid var(--color-steel-dk)',
  };

  return (
    <section style={{ marginTop: 'var(--space-10)' }} aria-labelledby="compare-heading">
      <h2
        id="compare-heading"
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--weight-black)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-tight)',
          textAlign: 'center',
          color: 'var(--color-white)',
          marginBottom: 'var(--space-5)',
        }}
      >
        Compare plans
      </h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
          <thead>
            <tr>
              <th style={{ ...cellBase, textAlign: 'left', borderTop: 'none', color: 'var(--color-steel)' }}>
                Capability
              </th>
              {PLAN_CATALOG.map((plan) => (
                <th
                  key={plan.tier}
                  style={{
                    ...cellBase,
                    borderTop: 'none',
                    color: 'var(--color-white)',
                    fontWeight: 'var(--weight-bold)',
                  }}
                >
                  {plan.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td style={{ ...cellBase, textAlign: 'left', color: 'var(--color-white-dim)' }}>
                  {row.label}
                </td>
                {PLAN_CATALOG.map((plan) => (
                  <td key={plan.tier} style={{ ...cellBase, color: 'var(--color-white)' }}>
                    {row.availability[plan.tier] ? (
                      <span aria-label="included" title="Included">
                        <CheckIcon />
                      </span>
                    ) : (
                      <span aria-label="not included" style={{ color: 'var(--color-muted)' }}>
                        —
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// Common style for a full-width CTA button at the bottom of a tier card.
const btnFull = {
  width: '100%',
  display: 'block',
  marginTop: 'auto',
};
